import { useQuotesFetch } from "@/hooks/quote/useQuotesFetch";
import { useQuotesMutation } from "@/hooks/quote/useQuotesMutation.tsx";
import { useQuotesDelete } from "@/hooks/quote/useQuotesDelete.tsx";
import { UseQuotesOptions } from "@/types/quote";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

export function useQuotes(options?: UseQuotesOptions) {
  const queryClient = useQueryClient();
  
  if (!queryClient) {
    throw new Error("QueryClient not available in useQuotes");
  }

  const fetchOptions = options || {
    searchQuery: "",
    page: 1,
    pageSize: 10,
    sortField: 'created_at',
    sortOrder: 'desc'
  };
  
  // Use the debounced search query if available
  const { data, isLoading } = useQuotesFetch({
    ...fetchOptions,
    searchQuery: fetchOptions.debouncedSearchQuery || fetchOptions.searchQuery || "",
  });
  
  const mutation = useQuotesMutation();
  const deleteMutation = useQuotesDelete({ queryClient });

  // Function to get quote count by category ID
  const getQuoteCountByCategoryId = useCallback(async (categoryId: string) => {
    const { count, error } = await supabase
      .from("quote_categories")
      .select("*", { count: "exact", head: false })
      .eq("category_id", categoryId);
      
    if (error) {
      console.error("Error getting quote count:", error);
      return 0;
    }
    
    return count || 0;
  }, []);

  return {
    quotes: data?.quotes || [],
    totalPages: data?.totalPages || 1,
    currentPage: data?.currentPage || 1,
    isLoading,
    mutation,
    deleteMutation,
    getQuoteCountByCategoryId
  };
}
